import { FileText, Video, Lock, Eye, RefreshCw, AlertCircle } from 'lucide-react'
import { useCompanyContent } from '@/hooks/useCompanyContent'
import { useCompanyStats } from '@/hooks/useCompanyStats'
import { RecentContent } from '@/components/dashboard/RecentContent'
import { UserAnalytics } from '@/components/analytics/UserAnalytics'
import { CARD_CLASSES, DASHBOARD_CONTAINER_CLASSES, DASHBOARD_SPACING } from '@/lib/constants/spacing'

interface CompanyUserDashboardProps {
  userName?: string
  companyName?: string
}

export function CompanyUserDashboard({ userName: _userName = 'User', companyName: _companyName = 'Your Company' }: CompanyUserDashboardProps) {
  const { content, loading: contentLoading, error: contentError, refetch: refetchContent } = useCompanyContent()
  const { stats, loading: statsLoading, error: statsError, refetch: refetchStats } = useCompanyStats()

  const loading = contentLoading || statsLoading
  const error = contentError || statsError

  const handleRetry = () => {
    refetchContent()
    refetchStats()
  }

  // Loading state
  if (loading) {
    return (
      <div className={DASHBOARD_CONTAINER_CLASSES}>
        {/* Content Cards Skeleton */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_COMPACT}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg animate-pulse w-10 h-10`}></div>
                <div className="h-5 bg-muted rounded animate-pulse w-16"></div>
              </div>
              <div className="h-5 bg-muted rounded animate-pulse w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded animate-pulse w-full mb-3"></div>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded animate-pulse w-1/2"></div>
                <div className="h-3 bg-muted rounded animate-pulse w-1/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={DASHBOARD_CONTAINER_CLASSES}>
        <div className={`${CARD_CLASSES} bg-destructive/10 border-destructive/20 ${DASHBOARD_SPACING.CARD_PADDING}`}>
          <div className={`flex items-start ${DASHBOARD_SPACING.LIST_ITEM_GAP}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-destructive/20 rounded-lg`}>
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Dashboard</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className={`inline-flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.BUTTON_PADDING} bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors`}
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={DASHBOARD_CONTAINER_CLASSES}>
      {/* Read-Only Notice */}
      <div className={`${CARD_CLASSES} bg-muted/50 ${DASHBOARD_SPACING.CARD_PADDING_DENSE} flex items-start ${DASHBOARD_SPACING.LIST_ITEM_GAP}`}>
        <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-muted rounded-lg`}>
          <Lock className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">Read-Only Access Mode</h3>
          <p className="text-sm text-muted-foreground">
            You can view and download all company content, but editing and uploading are restricted to company administrators.
            If you need to make changes, please reach out to your company admin.
          </p>
        </div>
      </div>

      {/* Recent Content Section */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <RecentContent
          content={content}
          maxItems={6}
          emptyMessage="No content available yet. Contact your company administrator to add materials."
          showViewAll={true}
          viewAllLink="/content?filter=company"
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${CARD_CLASSES} p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.presentationCount || 0}</p>
              <p className="text-sm text-muted-foreground">Presentations</p>
            </div>
          </div>
        </div>
        <div className={`${CARD_CLASSES} p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats?.pdfCount || 0}</p>
              <p className="text-sm text-muted-foreground">PDF Documents</p>
            </div>
          </div>
        </div>
        <div className={`${CARD_CLASSES} p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Video className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{(stats?.imageCount || 0) + (stats?.videoCount || 0)}</p>
              <p className="text-sm text-muted-foreground">Media Files</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Analytics Section */}
      <div className={`${CARD_CLASSES} p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Eye className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Your Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Personal activity and usage statistics
            </p>
          </div>
        </div>
        <UserAnalytics />
      </div>
    </div>
  )
}
